import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  Bell,
  BellOff,
  Building2,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import AnimatedLogo, { AnimatedFooter } from "../components/AnimatedLogo";
import api from "../lib/api";
import { formatDate } from "../lib/utils";

export default function MemberProfile() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset, watch, setValue } = useForm();

  useEffect(() => {
    const token = localStorage.getItem("tireo_member_token");
    if (!token) {
      navigate("/join");
      return;
    }
    loadProfile(token);
  }, []);

  const loadProfile = async (token) => {
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const [profileRes, subsRes] = await Promise.all([
        api.get("/member-portal/me"),
        api.get("/member-portal/subscriptions")
      ]);
      
      setMember(profileRes.data);
      setSubscriptions(subsRes.data);
      reset(profileRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("tireo_member_token");
        navigate("/join");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (data) => {
    setSaving(true);
    const token = localStorage.getItem("tireo_member_token");
    
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const res = await api.put("/member-portal/me", {
        name: data.name,
        phone: data.phone,
        accept_marketing: data.accept_marketing
      });
      
      setMember(res.data);
      localStorage.setItem("tireo_member", JSON.stringify(res.data));
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const toggleSubscription = async (organizerId) => {
    const token = localStorage.getItem("tireo_member_token");
    
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const res = await api.put(`/member-portal/subscriptions/${organizerId}`);
      
      // Update local state
      setSubscriptions(subs => 
        subs.map(s => 
          s.organizer_id === organizerId 
            ? { ...s, is_active: res.data.is_active }
            : s
        )
      );
      
      toast.success(res.data.is_active ? "Abonnement réactivé" : "Abonnement désactivé");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const unsubscribe = async (organizerId) => {
    const token = localStorage.getItem("tireo_member_token");
    
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      await api.delete(`/member-portal/subscriptions/${organizerId}`);
      
      setSubscriptions(subs => subs.filter(s => s.organizer_id !== organizerId));
      toast.success("Désabonnement effectué");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/play">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Mon Profil</h1>
          </div>
          <AnimatedLogo linkTo="/" showAnimation={false} size="small" variant="light" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSave)} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet</Label>
                  <Input
                    id="name"
                    {...register("name")}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={member?.email}
                    disabled
                    className="h-11 bg-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    {...register("phone")}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Membre depuis</Label>
                  <Input
                    value={formatDate(member?.created_at)}
                    disabled
                    className="h-11 bg-slate-100"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Marketing Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Préférences de communication
            </CardTitle>
            <CardDescription>
              Gérez vos préférences pour recevoir des informations sur les jeux et offres
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
              <div>
                <p className="font-medium">Recevoir des offres exclusives</p>
                <p className="text-sm text-muted-foreground">
                  Soyez informé(e) des nouveaux jeux, tirages et offres partenaires
                </p>
              </div>
              <Switch
                checked={watch("accept_marketing")}
                onCheckedChange={(checked) => {
                  setValue("accept_marketing", checked);
                  handleSubmit(onSave)();
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Organizer Subscriptions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Mes abonnements organisateurs ({subscriptions.filter(s => s.is_active).length})
            </CardTitle>
            <CardDescription>
              Gérez les organisateurs dont vous recevez les communications.
              <br />
              <span className="text-xs text-muted-foreground">
                Vous pouvez aussi écrire à <a href="mailto:stop@tireo.app" className="text-primary underline">stop@tireo.app</a> pour toute demande
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun abonnement
              </p>
            ) : (
              <div className="space-y-3">
                {subscriptions.map(sub => (
                  <div 
                    key={sub.organizer_id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium">{sub.organizer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Abonné depuis {formatDate(sub.subscribed_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge variant={sub.is_active ? "default" : "secondary"} className="gap-1">
                        {sub.is_active ? (
                          <>
                            <CheckCircle2 className="w-3 h-3" />
                            Actif
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3" />
                            Inactif
                          </>
                        )}
                      </Badge>
                      
                      <Switch
                        checked={sub.is_active}
                        onCheckedChange={() => toggleSubscription(sub.organizer_id)}
                      />
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => unsubscribe(sub.organizer_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* GDPR Notice */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-blue-900 mb-1">Vos droits RGPD</h3>
                <p className="text-sm text-blue-800">
                  Conformément au RGPD, vous pouvez à tout moment demander l'accès, la rectification 
                  ou la suppression de vos données personnelles. Pour toute demande, contactez-nous à{" "}
                  <a href="mailto:stop@tireo.app" className="underline font-medium">stop@tireo.app</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6">
          <AnimatedFooter />
        </div>
      </footer>
    </div>
  );
}
